import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, IndianRupee, Landmark, ReceiptText, UserCheck, UserX, Users, Wallet } from "lucide-react";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { StaffLabel } from "./StaffAvatar";
import { fmtDate, fmtINR } from "../lib/api";
import { useReceiptVisibility } from "../lib/receiptVisibility";   // Phase 169
import { ReceiptHiddenBadge } from "./ReceiptVisibilityNotice";   // Phase 169

/**
 * Phase 104 — shared building blocks for the per-property collection view.
 * Used by the Admin/Supervisor property sheet, the Tax Collector property page and the
 * Collections → By Property expandable rows, so every screen reads the same numbers.
 */

const MODE_STYLES = {
  cash: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20",
  upi: "bg-primary/10 text-primary border-primary/20",
  cheque: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
  "demand draft": "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
};
export const paymentModeClass = (m) => MODE_STYLES[String(m || "").toLowerCase()] || "bg-muted text-foreground border-border";
export const isLegacyPayment = (p) => p?.source === "legacy_import" || p?.collection_channel === "import";

const Tile = ({ label, value, tone, icon: Icon, sub, testId }) => (
  <div className={`rounded-lg border px-3 py-2.5 min-w-0 ${tone.box}`} data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground leading-tight">{label}</p>
      <Icon className={`h-3.5 w-3.5 shrink-0 ${tone.icon}`} aria-hidden="true" />
    </div>
    <p className={`text-base sm:text-lg font-semibold tabular-nums font-heading mt-1 leading-tight break-words ${tone.value}`}>{fmtINR(value || 0)}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sub}</p>}
  </div>
);

const TONES = {
  demand: { box: "bg-card", icon: "text-primary", value: "text-foreground" },
  collected: { box: "bg-[hsl(var(--success))]/5 border-[hsl(var(--success))]/20", icon: "text-[hsl(var(--success))]", value: "text-[hsl(var(--success))]" },
  balance: { box: "bg-destructive/5 border-destructive/20", icon: "text-destructive", value: "text-destructive" },
  settled: { box: "bg-[hsl(var(--success))]/5 border-[hsl(var(--success))]/20", icon: "text-[hsl(var(--success))]", value: "text-[hsl(var(--success))]" },
};

/** Total demand / Total collected / Balance due tiles + advance-credit note. */
export const CollectionSummaryCards = ({ summary, testId = "collection-summary" }) => {
  if (!summary) return null;
  const settled = summary.has_demand && summary.balance_due <= 0.004;
  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-3 gap-2">
        <Tile label="Total demand" value={summary.total_demand} tone={TONES.demand} icon={Landmark} testId={`${testId}-demand`}
          sub={summary.demands_count ? `${summary.demands_count} demand line${summary.demands_count === 1 ? "" : "s"}` : "no demand raised"} />
        <Tile label="Collected so far" value={summary.total_collected} tone={TONES.collected} icon={IndianRupee} testId={`${testId}-collected`}
          sub={summary.payments_count ? `${summary.payments_count} receipt${summary.payments_count === 1 ? "" : "s"}${summary.last_payment_date ? ` • last ${fmtDate(summary.last_payment_date)}` : ""}` : "no payments yet"} />
        <Tile label={settled ? "Balance due" : "Remaining balance"} value={summary.balance_due} tone={settled ? TONES.settled : TONES.balance}
          icon={Wallet} testId={`${testId}-balance`} sub={settled ? "fully settled" : summary.has_demand ? "outstanding" : "nothing due"} />
      </div>
      {summary.advance_credit > 0.004 && (
        <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${testId}-advance`}>
          <Badge variant="outline" className="text-[10px] font-normal bg-primary/5 text-primary border-primary/20">Credit</Badge>
          <span><span className="font-medium text-foreground tabular-nums" data-testid={`${testId}-advance-amount`}>{fmtINR(summary.advance_credit)}</span> paid over and above the demand (advance or overpayment after a demand correction) is held as credit against this property and is included in “Collected so far”.</span>
        </p>
      )}
    </div>
  );
};

/** Who is accountable for collecting on this property: the assigned collector, else the zone supervisor(s). */
export const ResponsibleStaff = ({ code, responsible, testId = "responsible-staff", compact = false }) => {
  if (!responsible) return null;
  const staff = responsible.staff || [];
  if (responsible.type === "collector" && staff[0]) {
    const s = staff[0];
    return (
      <div className={`flex items-start gap-2 ${compact ? "" : "rounded-lg border bg-muted/40 px-3 py-2"}`} data-testid={testId} data-responsible-type="collector">
        <UserCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 text-sm">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Assigned tax collector</p>
          <p className="font-medium truncate"><StaffLabel code={code} userId={s.id} name={s.name} empCode={s.employee_id} testId={`${testId}-name`} />
            {s.is_active === false && <span className="ml-1 text-[11px] text-muted-foreground">· inactive</span>}
          </p>
          {!compact && responsible.assignment_status && (
            <p className="text-[11px] text-muted-foreground capitalize">Assignment: {String(responsible.assignment_status).replace(/_/g, " ")}</p>
          )}
        </div>
      </div>
    );
  }
  if (responsible.type === "supervisor" && staff.length) {
    return (
      <div className={`flex items-start gap-2 ${compact ? "" : "rounded-lg border bg-muted/40 px-3 py-2"}`} data-testid={testId} data-responsible-type="supervisor">
        <Users className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 text-sm">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">No collector assigned — zone supervisor{staff.length === 1 ? "" : "s"}</p>
          <p className="font-medium">
            {staff.map((s, i) => (
              <span key={s.id}>{i > 0 && <span className="text-muted-foreground">, </span>}
                <StaffLabel code={code} userId={s.id} name={s.name} empCode={s.employee_id} testId={`${testId}-name-${i}`} />
              </span>
            ))}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex items-start gap-2 ${compact ? "" : "rounded-lg border border-dashed px-3 py-2"}`} data-testid={testId} data-responsible-type="none">
      <UserX className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 text-sm">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Responsible staff</p>
        <p className="text-muted-foreground">Unassigned — no collector assigned and no supervisor mapped to this zone.</p>
      </div>
    </div>
  );
};

/** Inline one-liner for dense table cells. */
export const ResponsibleInline = ({ code, responsible, testId }) => {
  const staff = responsible?.staff || [];
  if (responsible?.type === "collector" && staff[0]) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm min-w-0" data-testid={testId} data-responsible-type="collector">
        <UserCheck className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />
        <StaffLabel code={code} userId={staff[0].id} name={staff[0].name} empCode={staff[0].employee_id} className="truncate" />
      </span>
    );
  }
  if (responsible?.type === "supervisor" && staff.length) {
    return (
      <span className="inline-flex items-start gap-1.5 text-sm min-w-0" data-testid={testId} data-responsible-type="supervisor">
        <Users className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0 mt-0.5" aria-hidden="true" />
        <span className="min-w-0">
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground leading-none mb-0.5">Supervisor</span>
          {staff.map((s, i) => (
            <span key={s.id}>{i > 0 && ", "}<StaffLabel code={code} userId={s.id} name={s.name} empCode={s.employee_id} /></span>
          ))}
        </span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground" data-testid={testId} data-responsible-type="none">Unassigned</span>;
};

const CollectedBy = ({ code, p }) => {
  if (isLegacyPayment(p)) {
    // Phase 135: imports credited to the property's assigned staff show the staff + an "Imported" marker
    if (p.collected_by || (p.collected_by_name && p.collected_by_name !== "Imported (Legacy)")) {
      return (
        <span className="inline-flex flex-wrap items-center gap-1">
          <StaffLabel code={code} userId={p.collected_by} name={p.collected_by_name} empCode={p.collected_by_employee_id} fallback="Staff" />
          <Badge variant="outline" className="text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200">Imported</Badge>
        </span>
      );
    }
    return <Badge variant="outline" className="text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200">Imported (Legacy)</Badge>;
  }
  if (p.collected_by || p.collected_by_name) {
    return <StaffLabel code={code} userId={p.collected_by} name={p.collected_by_name} empCode={p.collected_by_employee_id} fallback="Staff" />;
  }
  return <span className="text-muted-foreground">Online / Citizen</span>;
};

/** Full receipt-wise history of one property: date, receipt, mode, collected by, amount. */
export const PaymentHistoryTable = ({ code, payments, testId = "payment-history", emptyText = "No payments recorded for this property yet.", showCollector = true }) => {
  const rv = useReceiptVisibility(code);   // Phase 169: receipt links follow the municipality's visibility rule for this role
  const rows = payments || [];
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-6 text-center" data-testid={`${testId}-empty`}>
        <ReceiptText className="h-5 w-5 text-muted-foreground mx-auto mb-1.5" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      </div>
    );
  }
  const total = rows.reduce((s, p) => s + (p.status === "success" || !p.status ? Number(p.amount || 0) : 0), 0);
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Date</TableHead>
              <TableHead>Receipt no.</TableHead>
              <TableHead>Mode</TableHead>
              {showCollector && <TableHead className="hidden sm:table-cell">Collected by</TableHead>}
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} className={p.status && p.status !== "success" ? "opacity-60" : ""} data-testid={`${testId}-row-${p.receipt_number || p.id}`}>
                <TableCell className="whitespace-nowrap align-top">
                  <p className="text-sm tabular-nums">{fmtDate(p.payment_date)}</p>
                  {p.payment_time && <p className="text-[11px] text-muted-foreground tabular-nums">{String(p.payment_time).slice(0, 5)}</p>}
                </TableCell>
                <TableCell className="font-mono text-sm align-top whitespace-nowrap">
                  {p.receipt_number ? (
                    (p.receipt_visible !== false && rv.canView(p.payment_date)) ? (
                      <Link to={`/${code}/receipt/${p.receipt_number}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`${testId}-receipt-link-${p.receipt_number}`}>
                        {p.receipt_number} <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    ) : (
                      <span className="inline-flex flex-wrap items-center gap-1.5 text-muted-foreground" data-testid={`${testId}-receipt-hidden-${p.receipt_number}`}>
                        {p.receipt_number} <ReceiptHiddenBadge testId={`${testId}-receipt-hidden-badge-${p.receipt_number}`} />
                      </span>
                    )
                  ) : <span className="text-muted-foreground">—</span>}
                  {p.legacy_receipt_number && <p className="text-[11px] text-muted-foreground font-sans">Old receipt: {p.legacy_receipt_number}</p>}
                  {p.status === "replaced" ? (
                    <p className="text-[11px] text-amber-800 font-sans" data-testid={`${testId}-replaced-${p.id}`}>
                      Replaced by import{p.replaced_by_receipt_number ? <> → <span className="font-mono">{p.replaced_by_receipt_number}</span></> : null}
                      {p.original_receipt_number ? <span className="text-muted-foreground"> · was {p.original_receipt_number}</span> : null}
                    </p>
                  ) : p.status && p.status !== "success" && <p className="text-[11px] text-destructive font-sans capitalize">{p.status}</p>}
                  {p.replaced_receipt_number && p.status === "success" && (
                    <p className="text-[11px] text-muted-foreground font-sans" data-testid={`${testId}-replaces-${p.id}`}>Replaces receipt {p.replaced_receipt_number}</p>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  <Badge variant="outline" className={`font-medium ${paymentModeClass(p.payment_mode)}`}>{p.payment_mode || "Unknown"}</Badge>
                  {p.advance_amount > 0.004 && <p className="text-[11px] text-muted-foreground mt-1">incl. {fmtINR(p.advance_amount)} advance</p>}
                </TableCell>
                {showCollector && <TableCell className="hidden sm:table-cell text-sm align-top"><CollectedBy code={code} p={p} /></TableCell>}
                <TableCell className="text-right tabular-nums font-medium whitespace-nowrap align-top">{fmtINR(p.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/40 text-xs" data-testid={`${testId}-footer`}>
        <span className="text-muted-foreground">{rows.length} receipt{rows.length === 1 ? "" : "s"}</span>
        <span className="tabular-nums font-medium">Total {fmtINR(total)}</span>
      </div>
    </div>
  );
};

/** Summary tiles + responsible staff + history in one block (property pages). */
export const PropertyCollectionPanel = ({ code, summary, responsible, payments, testId = "property-collection", showCollector = true }) => (
  <div className="space-y-3" data-testid={testId}>
    <CollectionSummaryCards summary={summary} testId={`${testId}-summary`} />
    <ResponsibleStaff code={code} responsible={responsible} testId={`${testId}-responsible`} />
    <div>
      <p className="font-medium text-sm mb-2">Collection history</p>
      <PaymentHistoryTable code={code} payments={payments} testId={`${testId}-history`} showCollector={showCollector} />
    </div>
  </div>
);
